// src/pages/PerfilCliente.jsx
import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { db } from "../firebase";
import { useCotizaciones } from "../hooks/useCotizaciones";
import { usePedidos } from "../hooks/usePedidos";
import { useHistorial } from "../hooks/useHistorial";
import { useActividadesCliente } from "../hooks/useActividadesCliente";
import { useReunionesCliente } from "../hooks/useReuniones";
import toast from "react-hot-toast";

const ESTADOS_GANADA_COT   = ["Ganada", "Cerrada", "Aprobada"];
const ESTADOS_CERRADOS_COT = ["Ganada", "Cerrada", "Aprobada", "Perdida", "Rechazada", "Vencida"];

const ESTADO_COT = {
  "Pendiente":  { bg:"#E6F1FB", color:"#185FA5" },
  "Aprobada":   { bg:"#EAF3DE", color:"#3B6D11" },
  "Ganada":     { bg:"#E1F5EE", color:"#0F6E56" },
  "Cerrada":    { bg:"#E1F5EE", color:"#0F6E56" },
  "Perdida":    { bg:"#FCEBEB", color:"#A32D2D" },
  "Rechazada":  { bg:"#FCEBEB", color:"#A32D2D" },
  "Vencida":    { bg:"#FAEEDA", color:"#854F0B" },
};
const ESTADO_PED = {
  "Solicitud recibida": { bg:"#EEF2FF", color:"#3730A3" },
  "En preparación":     { bg:"#EDE9FE", color:"#5B21B6" },
  "En tránsito":        { bg:"#E6F1FB", color:"#185FA5" },
  "En reparto":         { bg:"#FAEEDA", color:"#854F0B" },
  "Entregado":          { bg:"#E1F5EE", color:"#0F6E56" },
  "Cancelado":          { bg:"#FCEBEB", color:"#A32D2D" },
};
const TIPO_ICON = { "Llamada":"📞","WhatsApp":"💬","Email":"📧","Reunión":"🤝","Visita en obra":"🏗","Otro":"📝" };

const TIPOS_OBRA = ["Vivienda","Infraestructura","Acabados","Saneamiento","Industrial","Comercial","Minería"];
const TAGS_PREDEFINIDOS = ["Lima","Huánuco","Junín","Pasco","Ucayali","Constructor","Contratista","Empresa Pública","SEACE","VIP","Recurrente"];

const fmtMoney = v => `S/ ${Number(v||0).toLocaleString("es-PE")}`;
const fmtFecha = f => { if (!f) return "—"; try { const [y,m,d]=f.split("-"); return `${d}/${m}/${y}`; } catch { return f; } };
const fmtTS    = ts => { if (!ts) return "—"; const d=ts.toDate?ts.toDate():new Date(ts); return d.toLocaleDateString("es-PE",{day:"numeric",month:"short",year:"numeric"}); };

const TABS = ["Resumen","Cotizaciones","Actividades","Pedidos","Historial","Reuniones","Perfil 360°"];

// ── Scoring color ────────────────────────────────────────────────────────────
function ScoringBar({ valor }) {
  const color = valor >= 70 ? "#1D9E75" : valor >= 40 ? "#D97706" : "#DC2626";
  return (
    <div style={{ display:"flex", alignItems:"center", gap:8 }}>
      <div style={{ flex:1, height:8, background:"#f1f5f9", borderRadius:4, overflow:"hidden" }}>
        <div style={{ width:`${valor}%`, height:"100%", background:color, borderRadius:4,
          transition:"width 600ms cubic-bezier(0.23,1,0.32,1)" }} />
      </div>
      <span style={{ fontSize:12, fontWeight:700, color, minWidth:36 }}>{valor}/100</span>
    </div>
  );
}

// ── Barras de frecuencia ─────────────────────────────────────────────────────
function FreqBar({ items, colorFn }) {
  const max = items[0]?.count || 1;
  return (
    <div style={{ display:"flex", flexDirection:"column", gap:5 }}>
      {items.slice(0,5).map(({ val, count }) => (
        <div key={val} style={{ display:"flex", alignItems:"center", gap:8 }}>
          <span style={{ fontSize:11, color:"#475569", width:160, flexShrink:0, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{val}</span>
          <div style={{ flex:1, height:6, background:"#f1f5f9", borderRadius:3, overflow:"hidden" }}>
            <div style={{ width:`${(count/max)*100}%`, height:"100%", background:colorFn(val),
              borderRadius:3, transition:"width 600ms cubic-bezier(0.23,1,0.32,1)" }} />
          </div>
          <span style={{ fontSize:10, color:"#94a3b8", minWidth:16, textAlign:"right" }}>{count}</span>
        </div>
      ))}
    </div>
  );
}

export default function PerfilCliente() {
  const { id }   = useParams();
  const navigate = useNavigate();

  const [cliente,       setCliente]      = useState(null);
  const [loading,       setLoading]      = useState(true);
  const [tab,           setTab]          = useState("Resumen");
  const [editActId,     setEditActId]    = useState(null);
  const [editActForm,   setEditActForm]  = useState({});
  const [confirmDelId,  setConfirmDelId] = useState(null);

  // Bloque A — edición perfil estratégico
  const [editandoPerfil,  setEditandoPerfil]  = useState(false);
  const [perfilForm,      setPerfilForm]      = useState({});
  const [guardandoPerfil, setGuardandoPerfil] = useState(false);

  const { cotizaciones }      = useCotizaciones();
  const { pedidos }           = usePedidos();
  const { historial }         = useHistorial();
  const { actividades, loading: loadAct, actualizarActividad, eliminarActividad } = useActividadesCliente(id);
  const { reuniones }         = useReunionesCliente(id);

  useEffect(() => {
    if (!id) return;
    getDoc(doc(db, "clientes", id)).then(snap => {
      if (snap.exists()) {
        const data = { id: snap.id, ...snap.data() };
        setCliente(data);
        setPerfilForm(data.perfilConstructora || {});
      }
      setLoading(false);
    });
  }, [id]);

  const cotsCli    = useMemo(() => cotizaciones.filter(c => c.clienteId === id), [cotizaciones, id]);
  const pedidosCli = useMemo(() => pedidos.filter(p => p.clienteId === id), [pedidos, id]);
  const histCli    = useMemo(() => historial.filter(h => h.clienteId === id || h.nombre === cliente?.nombre), [historial, id, cliente]);

  const stats = useMemo(() => ({
    totalCots:  cotsCli.length,
    cerradas:   cotsCli.filter(c => ESTADOS_GANADA_COT.includes(c.estado)).length,
    ingresos:   cotsCli.filter(c => ESTADOS_GANADA_COT.includes(c.estado)).reduce((s,c) => s+(Number(c.valor)||0), 0),
    pipeline:   cotsCli.filter(c => !ESTADOS_CERRADOS_COT.includes(c.estado)).reduce((s,c) => s+(Number(c.valor)||0), 0),
    pedidosAct: pedidosCli.filter(p => !["Entregado","Cancelado"].includes(p.estado)).length,
    conversion: cotsCli.length > 0 ? ((cotsCli.filter(c => ESTADOS_GANADA_COT.includes(c.estado)).length / cotsCli.length)*100).toFixed(0) : 0,
  }), [cotsCli, pedidosCli]);

  // ── Bloque B — comportamiento automático ────────────────────────────────────
  const comportamiento = useMemo(() => {
    const toMs = p => {
      if (!p.creadoEn) return null;
      const d = p.creadoEn?.toDate ? p.creadoEn.toDate() : new Date(p.creadoEn);
      return isNaN(d.getTime()) ? null : d.getTime();
    };
    const entregados = pedidosCli
      .filter(p => p.estado === "Entregado")
      .map(toMs).filter(Boolean).sort((a,b) => a-b);

    let frecuencia = null;
    if (entregados.length >= 2) {
      const gaps = [];
      for (let i = 1; i < entregados.length; i++) gaps.push(Math.round((entregados[i]-entregados[i-1])/86400000));
      frecuencia = Math.round(gaps.reduce((s,g) => s+g, 0) / gaps.length);
    }

    const ganadas    = cotsCli.filter(c => ESTADOS_GANADA_COT.includes(c.estado) && Number(c.valor) > 0);
    const ticketProm = ganadas.length > 0 ? Math.round(ganadas.reduce((s,c) => s+Number(c.valor),0) / ganadas.length) : 0;

    const now    = Date.now();
    const hace3m = now - 90*86400000;
    const hace6m = now - 180*86400000;
    const valRec = pedidosCli.filter(p => { const t=toMs(p); return t&&t>=hace3m&&p.estado==="Entregado"; }).reduce((s,p)=>s+(Number(p.valor)||0),0);
    const valAnt = pedidosCli.filter(p => { const t=toMs(p); return t&&t>=hace6m&&t<hace3m&&p.estado==="Entregado"; }).reduce((s,p)=>s+(Number(p.valor)||0),0);

    const tendencia = valAnt === 0
      ? (valRec > 0 ? "creciendo" : "sin datos")
      : valRec > valAnt*1.1 ? "creciendo"
      : valRec < valAnt*0.9 ? "bajando" : "estable";
    const tendenciaPct = valAnt > 0 ? Math.round(((valRec-valAnt)/valAnt)*100) : 0;

    const convScore   = Math.min(100, Number(stats.conversion||0));
    // frecScore queda null (no 0) cuando el cliente todavía no tiene 2+ pedidos entregados —
    // es "sin dato todavía", no "mal desempeño". Forzarlo a 0 penalizaba a clientes nuevos.
    const frecScore   = frecuencia != null ? Math.max(0, Math.min(100, 100 - frecuencia)) : null;
    const ticketScore = Math.min(100, (ticketProm/50000)*100);

    // Promedio ponderado solo sobre los componentes que sí tienen dato — si falta uno
    // (cliente nuevo sin historial de frecuencia), su peso se redistribuye entre los
    // demás en vez de restar como si fuera un 0 real. Para un cliente con los 3 datos
    // disponibles el resultado es idéntico a la fórmula anterior (pesoTotal = 1).
    const componentesBase = [
      { label: "Conversión",        valor: convScore,   peso: 0.4 },
      { label: "Frecuencia compra", valor: frecScore,    peso: 0.3 },
      { label: "Ticket promedio",   valor: ticketScore,  peso: 0.3 },
    ].filter(c => c.valor != null);
    const pesoTotal = componentesBase.reduce((s, c) => s + c.peso, 0);
    const scoring = pesoTotal > 0
      ? Math.round(componentesBase.reduce((s, c) => s + c.valor * c.peso, 0) / pesoTotal)
      : 0;
    // pesoEfectivo = % real que aportó cada componente al resultado final, ya
    // redistribuido — esto es lo que se muestra en pantalla, no el peso "de catálogo".
    const componentes = componentesBase.map(c => ({ ...c, pesoEfectivo: Math.round(c.peso / pesoTotal * 100) }));
    const huboRedistribucion = componentesBase.length < 3;

    return { frecuencia, ticketProm, tendencia, tendenciaPct, scoring, componentes, huboRedistribucion };
  }, [cotsCli, pedidosCli, stats]);

  // ── Bloque C — inteligencia cualitativa desde reuniones ─────────────────────
  const inteligencia = useMemo(() => {
    const contar = (key) => {
      const counts = {};
      reuniones.forEach(r => (r[key]||[]).forEach(v => { counts[v] = (counts[v]||0)+1; }));
      return Object.entries(counts).sort((a,b)=>b[1]-a[1]).map(([val,count])=>({val,count}));
    };
    const proximas = reuniones.flatMap(r => r.proximasCompras||[]);
    return {
      dolores:      contar("dolores"),
      objeciones:   contar("objeciones"),
      prioridades:  contar("prioridades"),
      proximasCompras: proximas,
      probProm: reuniones.length > 0
        ? Math.round(reuniones.reduce((s,r)=>s+(r.probabilidadCierre||0),0)/reuniones.length) : 0,
    };
  }, [reuniones]);

  // ── Guardar Bloque A ────────────────────────────────────────────────────────
  const guardarPerfil = async () => {
    setGuardandoPerfil(true);
    try {
      await updateDoc(doc(db,"clientes",id), { perfilConstructora: perfilForm });
      setCliente(c => ({ ...c, perfilConstructora: perfilForm }));
      setEditandoPerfil(false);
      toast.success("✅ Perfil estratégico guardado");
    } catch {
      toast.error("Error al guardar");
    }
    setGuardandoPerfil(false);
  };

  const toggleTipoObra = (t) =>
    setPerfilForm(f => ({
      ...f,
      tipoObra: (f.tipoObra||[]).includes(t)
        ? (f.tipoObra||[]).filter(x=>x!==t)
        : [...(f.tipoObra||[]), t],
    }));

  if (loading) return <div style={{textAlign:"center",padding:60,color:"#94a3b8"}}>Cargando perfil...</div>;
  if (!cliente) return <div style={{textAlign:"center",padding:60,color:"#DC2626"}}>Cliente no encontrado.</div>;

  const hoyD = new Date();
  const hoy  = `${hoyD.getFullYear()}-${String(hoyD.getMonth()+1).padStart(2,"0")}-${String(hoyD.getDate()).padStart(2,"0")}`;
  const vencido = cliente.proximoSeguimiento && cliente.proximoSeguimiento < hoy;

  const inp = { padding:"7px 10px", border:"1px solid #e2e8f0", borderRadius:7, fontSize:12, outline:"none", width:"100%", boxSizing:"border-box", fontFamily:"inherit" };
  const lbl = { fontSize:10, fontWeight:600, color:"#64748b", display:"block", marginBottom:4 };

  return (
    <div style={{maxWidth:960,margin:"0 auto",padding:"0 8px",display:"flex",flexDirection:"column",gap:16}}>
      <style>{`
        .perf-card { transition: box-shadow 200ms cubic-bezier(0.23,1,0.32,1), transform 200ms cubic-bezier(0.23,1,0.32,1); }
        .perf-card:hover { box-shadow: 0 4px 16px rgba(58,60,122,0.10); transform: translateY(-1px); }
      `}</style>

      {/* Header */}
      <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",flexWrap:"wrap",gap:10}}>
        <div style={{display:"flex",alignItems:"center",gap:14}}>
          <div style={{width:52,height:52,borderRadius:"50%",background:"#E6F1FB",color:"#185FA5",display:"flex",alignItems:"center",justifyContent:"center",fontSize:20,fontWeight:700,flexShrink:0}}>
            {cliente.nombre?.charAt(0)}
          </div>
          <div>
            <div style={{display:"flex",alignItems:"center",gap:8}}>
              <h1 style={{fontSize:22,fontWeight:700,color:"#1e293b",margin:0}}>{cliente.nombre}</h1>
              {cliente.estado && (
                <span style={{fontSize:10,fontWeight:600,padding:"2px 8px",borderRadius:8,...(ESTADO_COT[cliente.estado]||{bg:"#f1f5f9",color:"#475569"})}}>
                  {cliente.estado}
                </span>
              )}
              {comportamiento.scoring > 0 && (
                <span style={{fontSize:10,fontWeight:700,padding:"2px 8px",borderRadius:8,
                  background: comportamiento.scoring>=70?"#E1F5EE":comportamiento.scoring>=40?"#FAEEDA":"#FCEBEB",
                  color:      comportamiento.scoring>=70?"#0F6E56":comportamiento.scoring>=40?"#854F0B":"#A32D2D",
                }}>
                  ★ {comportamiento.scoring}
                </span>
              )}
            </div>
            <div style={{fontSize:13,color:"#64748b",marginTop:2}}>
              {cliente.empresa||"Sin empresa"}{cliente.ciudad?` · ${cliente.ciudad}`:""}{cliente.ruc?` · RUC ${cliente.ruc}`:""}
            </div>
          </div>
        </div>
        <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
          {cliente.telefono && (
            <button onClick={()=>window.open(`https://wa.me/51${cliente.telefono.replace(/\D/g,"")}?text=Hola ${cliente.nombre.split(" ")[0]},`,"_blank")}
              style={{fontSize:12,fontWeight:600,padding:"7px 14px",borderRadius:8,background:"#DCF8C6",color:"#128C7E",border:"none",cursor:"pointer"}}>
              📱 WhatsApp
            </button>
          )}
          {cliente.email && (
            <button onClick={()=>window.open(`mailto:${cliente.email}`)}
              style={{fontSize:12,fontWeight:600,padding:"7px 14px",borderRadius:8,background:"#E6F1FB",color:"#185FA5",border:"none",cursor:"pointer"}}>
              ✉ Email
            </button>
          )}
          <Link to="/reuniones"
            style={{fontSize:12,fontWeight:600,padding:"7px 14px",borderRadius:8,background:"#EDE9FE",color:"#7C3AED",textDecoration:"none"}}>
            + Reunión
          </Link>
          <Link to={`/clientes/${id}`}
            style={{fontSize:12,fontWeight:600,padding:"7px 14px",borderRadius:8,background:"#fff",color:"#3A3C7A",border:"1.5px solid #3A3C7A",textDecoration:"none"}}>
            ✏ Editar
          </Link>
          <button onClick={()=>navigate("/clientes")}
            style={{fontSize:12,padding:"7px 14px",borderRadius:8,background:"#f1f5f9",color:"#64748b",border:"none",cursor:"pointer"}}>
            ← Volver
          </button>
        </div>
      </div>

      {/* Tags */}
      <div style={{display:"flex",alignItems:"center",gap:6,flexWrap:"wrap"}}>
        {(cliente.tags||[]).map(tag => (
          <span key={tag} onClick={async()=>{
            const next = (cliente.tags||[]).filter(t=>t!==tag);
            await updateDoc(doc(db,"clientes",id),{tags:next});
            setCliente(c=>({...c,tags:next}));
          }} style={{fontSize:11,fontWeight:600,padding:"3px 10px",borderRadius:20,background:"#EEF2FF",color:"#3A3C7A",cursor:"pointer",display:"flex",alignItems:"center",gap:4}}>
            {tag} <span style={{fontSize:9,color:"#94a3b8"}}>×</span>
          </span>
        ))}
        {TAGS_PREDEFINIDOS.filter(t=>!(cliente.tags||[]).includes(t)).map(tag=>(
          <span key={tag} onClick={async()=>{
            const next = [...(cliente.tags||[]),tag];
            await updateDoc(doc(db,"clientes",id),{tags:next});
            setCliente(c=>({...c,tags:next}));
          }} style={{fontSize:11,padding:"3px 10px",borderRadius:20,background:"#f8fafc",color:"#94a3b8",cursor:"pointer",border:"1px dashed #e2e8f0"}}>
            + {tag}
          </span>
        ))}
      </div>

      {/* KPIs rápidos */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(6,1fr)",gap:8}}>
        {[
          {l:"Cotizaciones",    v:stats.totalCots,          c:"#3A3C7A"},
          {l:"Ganadas",         v:stats.cerradas,           c:"#1D9E75"},
          {l:"Conversión",      v:`${stats.conversion}%`,   c:Number(stats.conversion)>=30?"#1D9E75":"#DC2626"},
          {l:"Ingresos",        v:fmtMoney(stats.ingresos), c:"#1D9E75"},
          {l:"Pipeline activo", v:fmtMoney(stats.pipeline), c:"#D97706"},
          {l:"Pedidos activos", v:stats.pedidosAct,         c:"#8B5CF6"},
        ].map(k=>(
          <div key={k.l} className="perf-card" style={{background:"#fff",border:"0.5px solid #e2e8f0",borderTop:`3px solid ${k.c}`,borderRadius:10,padding:"10px 12px"}}>
            <div style={{fontSize:9,color:"#64748b",marginBottom:2}}>{k.l}</div>
            <div style={{fontSize:16,fontWeight:700,color:"#1e293b"}}>{k.v}</div>
          </div>
        ))}
      </div>

      {/* Próximo seguimiento */}
      {cliente.proximoSeguimiento && (
        <div style={{background:vencido?"#FFF5F5":"#F0FDF8",border:`1px solid ${vencido?"#FECACA":"#BBF7D0"}`,borderRadius:10,padding:"10px 16px",display:"flex",alignItems:"center",gap:10}}>
          <span style={{fontSize:16}}>{vencido?"⚠":"📅"}</span>
          <div>
            <div style={{fontSize:11,fontWeight:600,color:vencido?"#DC2626":"#0F6E56"}}>
              {vencido?"Seguimiento vencido":"Próximo seguimiento"} · {fmtFecha(cliente.proximoSeguimiento)}
            </div>
            {cliente.responsable&&<div style={{fontSize:10,color:"#64748b"}}>Responsable: {cliente.responsable}</div>}
          </div>
          <Link to={`/clientes/${id}`} style={{marginLeft:"auto",fontSize:11,fontWeight:600,padding:"4px 10px",borderRadius:6,background:vencido?"#FECACA":"#BBF7D0",color:vencido?"#DC2626":"#0F6E56",textDecoration:"none"}}>
            Actualizar
          </Link>
        </div>
      )}

      {/* Tabs */}
      <div style={{display:"flex",gap:2,borderBottom:"1px solid #e2e8f0",flexWrap:"wrap"}}>
        {TABS.map(t=>(
          <button key={t} onClick={()=>setTab(t)} style={{
            padding:"8px 14px",fontSize:11,fontWeight:600,border:"none",cursor:"pointer",borderRadius:"8px 8px 0 0",
            background:tab===t?"#fff":"transparent",
            color:tab===t?"#3A3C7A":"#64748b",
            borderBottom:tab===t?"2px solid #3A3C7A":"2px solid transparent",
          }}>
            {t}
            {t==="Cotizaciones"&&cotsCli.length>0       && <span style={{marginLeft:4,fontSize:9,background:"#EEF2FF",color:"#3A3C7A",padding:"1px 5px",borderRadius:8}}>{cotsCli.length}</span>}
            {t==="Actividades"&&actividades.length>0     && <span style={{marginLeft:4,fontSize:9,background:"#EDE9FE",color:"#7C3AED",padding:"1px 5px",borderRadius:8}}>{actividades.length}</span>}
            {t==="Pedidos"&&pedidosCli.length>0          && <span style={{marginLeft:4,fontSize:9,background:"#E1F5EE",color:"#0F6E56",padding:"1px 5px",borderRadius:8}}>{pedidosCli.length}</span>}
            {t==="Historial"&&histCli.length>0           && <span style={{marginLeft:4,fontSize:9,background:"#FAEEDA",color:"#854F0B",padding:"1px 5px",borderRadius:8}}>{histCli.length}</span>}
            {t==="Reuniones"&&reuniones.length>0         && <span style={{marginLeft:4,fontSize:9,background:"#EDE9FE",color:"#7C3AED",padding:"1px 5px",borderRadius:8}}>{reuniones.length}</span>}
          </button>
        ))}
      </div>

      {/* Contenido del tab */}
      <div style={{background:"#fff",border:"0.5px solid #e2e8f0",borderRadius:10,padding:16}}>

        {/* ── RESUMEN ── */}
        {tab==="Resumen" && (
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:20}}>
            <div>
              <div style={{fontSize:11,fontWeight:700,color:"#3A3C7A",textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:10}}>Datos del cliente</div>
              <div style={{display:"flex",flexDirection:"column",gap:6}}>
                {[
                  ["Nombre",     cliente.nombre],
                  ["Empresa",    cliente.empresa],
                  ["RUC/DNI",    cliente.ruc],
                  ["Teléfono",   cliente.telefono],
                  ["Email",      cliente.email],
                  ["Ciudad",     cliente.ciudad],
                  ["Fuente",     cliente.fuente],
                  ["Prioridad",  cliente.prioridad],
                  ["Responsable",cliente.responsable],
                  ["Proyecto",   cliente.proyecto],
                ].filter(([,v])=>v).map(([l,v])=>(
                  <div key={l} style={{display:"flex",gap:8}}>
                    <span style={{fontSize:11,color:"#94a3b8",width:80,flexShrink:0}}>{l}</span>
                    <span style={{fontSize:12,color:"#1e293b",fontWeight:500}}>{v}</span>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <div style={{fontSize:11,fontWeight:700,color:"#3A3C7A",textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:10}}>Notas</div>
              <p style={{fontSize:12,color:"#475569",lineHeight:1.6,margin:0}}>{cliente.notas||<em style={{color:"#94a3b8"}}>Sin notas.</em>}</p>

              {/* Resumen comportamiento */}
              {(comportamiento.frecuencia || comportamiento.ticketProm > 0) && (
                <div style={{marginTop:16}}>
                  <div style={{fontSize:11,fontWeight:700,color:"#3A3C7A",textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:8}}>Comportamiento</div>
                  <div style={{display:"flex",flexDirection:"column",gap:5}}>
                    {comportamiento.frecuencia && (
                      <div style={{display:"flex",gap:8}}>
                        <span style={{fontSize:11,color:"#94a3b8",width:120,flexShrink:0}}>Frec. compra</span>
                        <span style={{fontSize:12,fontWeight:600,color:"#1e293b"}}>cada {comportamiento.frecuencia} días</span>
                      </div>
                    )}
                    {comportamiento.ticketProm > 0 && (
                      <div style={{display:"flex",gap:8}}>
                        <span style={{fontSize:11,color:"#94a3b8",width:120,flexShrink:0}}>Ticket prom.</span>
                        <span style={{fontSize:12,fontWeight:600,color:"#1e293b"}}>{fmtMoney(comportamiento.ticketProm)}</span>
                      </div>
                    )}
                    <div style={{display:"flex",gap:8}}>
                      <span style={{fontSize:11,color:"#94a3b8",width:120,flexShrink:0}}>Tendencia</span>
                      <span style={{fontSize:12,fontWeight:600,color:comportamiento.tendencia==="creciendo"?"#1D9E75":comportamiento.tendencia==="bajando"?"#DC2626":"#64748b"}}>
                        {comportamiento.tendencia==="creciendo"?"↑":comportamiento.tendencia==="bajando"?"↓":"→"} {comportamiento.tendencia}
                        {comportamiento.tendenciaPct!==0&&` (${comportamiento.tendenciaPct>0?"+":""}${comportamiento.tendenciaPct}%)`}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Próximas compras detectadas en reuniones */}
              {inteligencia.proximasCompras.length > 0 && (
                <div style={{marginTop:16}}>
                  <div style={{fontSize:11,fontWeight:700,color:"#3A3C7A",textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:6}}>Próximas compras detectadas</div>
                  <div style={{display:"flex",flexWrap:"wrap",gap:4}}>
                    {inteligencia.proximasCompras.slice(0,4).map((c,i)=>(
                      <span key={i} style={{fontSize:11,fontWeight:600,padding:"3px 8px",borderRadius:8,background:"#E1F5EE",color:"#0F6E56"}}>
                        📦 {c.material}{c.cantidad?` · ${c.cantidad}`:""}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── COTIZACIONES ── */}
        {tab==="Cotizaciones" && (
          cotsCli.length===0 ? (
            <div style={{textAlign:"center",padding:32,color:"#94a3b8"}}>
              Sin cotizaciones.
              <button onClick={()=>navigate(`/cotizaciones?clienteId=${id}&clienteNombre=${encodeURIComponent(cliente.nombre)}&clienteEmpresa=${encodeURIComponent(cliente.empresa||"")}&responsable=${encodeURIComponent(cliente.responsable||"")}`)}
                style={{display:"block",margin:"12px auto 0",padding:"7px 16px",background:"#F97316",color:"#000",border:"none",borderRadius:8,fontSize:12,fontWeight:600,cursor:"pointer"}}>
                + Crear cotización
              </button>
            </div>
          ) : (
            <>
              <div style={{display:"flex",justifyContent:"flex-end",marginBottom:10}}>
                <button onClick={()=>navigate(`/cotizaciones?clienteId=${id}&clienteNombre=${encodeURIComponent(cliente.nombre)}&clienteEmpresa=${encodeURIComponent(cliente.empresa||"")}&responsable=${encodeURIComponent(cliente.responsable||"")}`)}
                  style={{padding:"6px 14px",background:"#F97316",color:"#000",border:"none",borderRadius:8,fontSize:12,fontWeight:700,cursor:"pointer"}}>
                  + Nueva cotización
                </button>
              </div>
              <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
                <thead>
                  <tr style={{background:"#f8fafc",borderBottom:"1px solid #e2e8f0"}}>
                    {["N°","Proyecto","Estado","Valor","Seguimiento","Responsable"].map(h=>(
                      <th key={h} style={{textAlign:"left",padding:"8px 10px",fontSize:10,fontWeight:600,color:"#94a3b8"}}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {cotsCli.map(c=>{
                    const es = ESTADO_COT[c.estado]||{bg:"#f1f5f9",color:"#475569"};
                    return (
                      <tr key={c.id} style={{borderBottom:"0.5px solid #f1f5f9"}}>
                        <td style={{padding:"8px 10px"}}>{c.numeroCot&&<span style={{fontSize:10,fontWeight:700,color:"#3A3C7A",background:"#EEF2FF",padding:"1px 5px",borderRadius:4}}>{c.numeroCot}</span>}</td>
                        <td style={{padding:"8px 10px",color:"#475569",maxWidth:150,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{c.proyecto||"—"}</td>
                        <td style={{padding:"8px 10px"}}><span style={{fontSize:10,fontWeight:600,padding:"2px 7px",borderRadius:7,...es}}>{c.estado}</span></td>
                        <td style={{padding:"8px 10px",fontWeight:700,color:"#1e293b"}}>{fmtMoney(c.valor)}</td>
                        <td style={{padding:"8px 10px",fontSize:11,color:"#475569"}}>{c.proximoSeguimiento?fmtFecha(c.proximoSeguimiento):"—"}</td>
                        <td style={{padding:"8px 10px",color:"#64748b"}}>{c.responsable||"—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </>
          )
        )}

        {/* ── ACTIVIDADES ── */}
        {tab==="Actividades" && (() => {
          // Feed unificado: actividades manuales + eventos de cotizaciones + eventos de pedidos
          const toTs = v => { if (!v) return 0; const d = v?.toDate ? v.toDate() : new Date(v); return isNaN(d) ? 0 : d.getTime(); };
          const fmtD = v => { if (!v) return "—"; const d = v?.toDate ? v.toDate() : new Date(v); return d.toLocaleDateString("es-PE",{day:"numeric",month:"short",year:"numeric"}); };
          const eventosCots = cotsCli.map(c => ({
            _ts: toTs(c.creadoEn), _tipo: "cotizacion", _icono: "📋",
            _titulo: `Cotización ${c.numeroCot || ""}`,
            _desc: `${c.estado} · ${c.valor ? `S/ ${Number(c.valor).toLocaleString("es-PE")}` : "sin valor"}${c.proyecto ? ` · ${c.proyecto}` : ""}`,
            _fecha: fmtD(c.creadoEn),
          }));
          const eventosPeds = pedidosCli.map(p => ({
            _ts: toTs(p.creadoEn), _tipo: "pedido", _icono: "📦",
            _titulo: `Pedido ${p.numero || p.codigoSeguimiento || ""}`,
            _desc: `${p.estado || ""}${p.valor ? ` · S/ ${Number(p.valor).toLocaleString("es-PE")}` : ""}`,
            _fecha: fmtD(p.creadoEn),
          }));
          const eventosActs = actividades.map(a => ({
            ...a, _ts: toTs(a.fecha ? new Date(a.fecha) : a.creadoEn),
            _tipo: "actividad", _icono: TIPO_ICON[a.tipo] || "📝",
            _titulo: a.tipo, _desc: a.descripcion, _fecha: fmtD(a.fecha ? new Date(a.fecha) : a.creadoEn),
            _editable: true,
          }));
          const feed = [...eventosCots, ...eventosPeds, ...eventosActs].sort((a,b) => b._ts - a._ts);

          return (
          <div>
            <div style={{display:"flex",justifyContent:"flex-end",marginBottom:10}}>
              <Link to={`/clientes/${id}`} style={{padding:"6px 14px",background:"#7C3AED",color:"#fff",borderRadius:8,fontSize:12,fontWeight:600,textDecoration:"none"}}>
                + Registrar actividad
              </Link>
            </div>
            {loadAct ? <div style={{textAlign:"center",color:"#94a3b8",padding:20}}>Cargando...</div>
            : feed.length===0 ? <div style={{textAlign:"center",color:"#94a3b8",padding:24}}>Sin actividad registrada.</div>
            : (
              <div style={{display:"flex",flexDirection:"column",gap:0,position:"relative"}}>
                <div style={{position:"absolute",left:20,top:8,bottom:8,width:2,background:"#e2e8f0",borderRadius:1}} />
                {feed.map((a, idx) => {
                  const bgTipo = { cotizacion:"#EEF2FF", pedido:"#E1F5EE", actividad:"#EDE9FE" }[a._tipo] || "#f1f5f9";
                  const colTipo = { cotizacion:"#3A3C7A", pedido:"#0F6E56", actividad:"#7C3AED" }[a._tipo] || "#475569";
                  if (a._editable) {
                    const editando = editActId===a.id;
                    const confirmando = confirmDelId===a.id;
                    return (
                    <div key={a.id||idx} style={{display:"flex",gap:12,paddingBottom:14,paddingLeft:8}}>
                      <div style={{width:24,height:24,borderRadius:"50%",background:bgTipo,display:"flex",alignItems:"center",justifyContent:"center",fontSize:12,flexShrink:0,zIndex:1,marginTop:2}}>{a._icono}</div>
                      <div style={{flex:1,background:"#f8fafc",borderRadius:8,padding:"10px 14px",border:"0.5px solid #e2e8f0"}}>
                        {!editando && (
                          <>
                            <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4,flexWrap:"wrap"}}>
                              <span style={{fontSize:11,fontWeight:700,color:colTipo}}>{a._titulo}</span>
                              <span style={{fontSize:10,color:"#94a3b8"}}>{a._fecha}</span>
                              {a.fuente==="agente"&&<span style={{fontSize:9,fontWeight:700,padding:"1px 5px",borderRadius:4,background:"#EEF2FF",color:"#3A3C7A"}}>🤖</span>}
                              <div style={{marginLeft:"auto",display:"flex",gap:5}}>
                                <button onClick={()=>{ setEditActId(a.id); setEditActForm({tipo:a.tipo||"Llamada",fecha:a.fecha||"",descripcion:a.descripcion||"",resultado:a.resultado||"",proximoContacto:a.proximoContacto||""}); setConfirmDelId(null); }}
                                  style={{fontSize:10,padding:"2px 8px",borderRadius:5,background:"#EEF2FF",color:"#3A3C7A",border:"none",cursor:"pointer",fontWeight:600}}>✏</button>
                                {confirmando ? (
                                  <>
                                    <button onClick={()=>{ eliminarActividad(a.id); setConfirmDelId(null); }} style={{fontSize:10,padding:"2px 8px",borderRadius:5,background:"#DC2626",color:"#fff",border:"none",cursor:"pointer",fontWeight:600}}>Sí</button>
                                    <button onClick={()=>setConfirmDelId(null)} style={{fontSize:10,padding:"2px 8px",borderRadius:5,background:"#f1f5f9",color:"#64748b",border:"none",cursor:"pointer"}}>No</button>
                                  </>
                                ) : (
                                  <button onClick={()=>setConfirmDelId(a.id)} style={{fontSize:10,padding:"2px 8px",borderRadius:5,background:"#FEE2E2",color:"#DC2626",border:"none",cursor:"pointer",fontWeight:600}}>×</button>
                                )}
                              </div>
                            </div>
                            <p style={{fontSize:12,color:"#1e293b",margin:"0 0 4px"}}>{a._desc}</p>
                            {a.resultado&&<p style={{fontSize:11,color:"#64748b",margin:"0 0 4px",fontStyle:"italic"}}>→ {a.resultado}</p>}
                          </>
                        )}
                        {editando && (
                          <div style={{display:"flex",flexDirection:"column",gap:8}}>
                            <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                              {["Llamada","WhatsApp","Email","Reunión","Visita en obra","Otro"].map(t=>(
                                <button key={t} onClick={()=>setEditActForm(f=>({...f,tipo:t}))} style={{
                                  fontSize:11,padding:"3px 10px",borderRadius:6,border:"none",cursor:"pointer",fontWeight:600,
                                  background:editActForm.tipo===t?"#7C3AED":"#f1f5f9",
                                  color:editActForm.tipo===t?"#fff":"#475569",
                                }}>{TIPO_ICON[t]} {t}</button>
                              ))}
                            </div>
                            <textarea value={editActForm.descripcion} onChange={e=>setEditActForm(f=>({...f,descripcion:e.target.value}))}
                              placeholder="¿Qué pasó?" rows={2}
                              style={{width:"100%",padding:"7px 10px",border:"1px solid #e2e8f0",borderRadius:6,fontSize:12,fontFamily:"inherit",resize:"none",outline:"none",boxSizing:"border-box"}} />
                            <div style={{display:"flex",justifyContent:"flex-end",gap:8}}>
                              <button onClick={()=>setEditActId(null)} style={{fontSize:11,padding:"5px 12px",borderRadius:6,background:"#f1f5f9",color:"#475569",border:"none",cursor:"pointer"}}>Cancelar</button>
                              <button onClick={async()=>{ await actualizarActividad(a.id,editActForm); setEditActId(null); }}
                                style={{fontSize:11,padding:"5px 14px",borderRadius:6,background:"#7C3AED",color:"#fff",border:"none",cursor:"pointer",fontWeight:700}}>
                                Guardar
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );}
                  return (
                    <div key={idx} style={{display:"flex",gap:12,paddingBottom:14,paddingLeft:8}}>
                      <div style={{width:24,height:24,borderRadius:"50%",background:bgTipo,display:"flex",alignItems:"center",justifyContent:"center",fontSize:12,flexShrink:0,zIndex:1,marginTop:2}}>{a._icono}</div>
                      <div style={{flex:1,background:"#f8fafc",borderRadius:8,padding:"10px 14px",border:"0.5px solid #e2e8f0"}}>
                        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:3,flexWrap:"wrap"}}>
                          <span style={{fontSize:11,fontWeight:700,color:colTipo}}>{a._titulo}</span>
                          <span style={{fontSize:10,color:"#94a3b8"}}>{a._fecha}</span>
                        </div>
                        <p style={{fontSize:12,color:"#475569",margin:0}}>{a._desc}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          );
        })()}

        {/* LEGACY REMOVED — handlers (editActId/confirmDelId) se mantienen en el feed unificado */}
        {false && (
          <div>
            {actividades.map(a => {
                  const editando   = editActId===a.id;
                  const confirmando = confirmDelId===a.id;
                  return (
                    <div key={a.id} style={{background:"#f8fafc",borderRadius:8,padding:"10px 14px",display:"flex",gap:10,border:"0.5px solid #e2e8f0"}}>
                      <span style={{fontSize:18,flexShrink:0}}>{TIPO_ICON[a.tipo]||"📝"}</span>
                      <div style={{flex:1}}>
                        {!editando && (
                          <>
                            <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4,flexWrap:"wrap"}}>
                              <span style={{fontSize:11,fontWeight:700,color:"#7C3AED"}}>{a.tipo}</span>
                              <span style={{fontSize:10,color:"#94a3b8"}}>{fmtFecha(a.fecha)}</span>
                              {a.fuente==="agente"&&<span style={{fontSize:9,fontWeight:700,padding:"1px 5px",borderRadius:4,background:"#EEF2FF",color:"#3A3C7A"}}>🤖 Agente</span>}
                              {a.responsable&&<span style={{fontSize:10,color:"#94a3b8"}}>· {a.responsable}</span>}
                              <div style={{marginLeft:"auto",display:"flex",gap:5}}>
                                <button onClick={()=>{ setEditActId(a.id); setEditActForm({tipo:a.tipo||"Llamada",fecha:a.fecha||"",descripcion:a.descripcion||"",resultado:a.resultado||"",proximoContacto:a.proximoContacto||""}); setConfirmDelId(null); }}
                                  style={{fontSize:10,padding:"2px 8px",borderRadius:5,background:"#EEF2FF",color:"#3A3C7A",border:"none",cursor:"pointer",fontWeight:600}}>
                                  Editar
                                </button>
                                {confirmando ? (
                                  <>
                                    <button onClick={()=>{ eliminarActividad(a.id); setConfirmDelId(null); }} style={{fontSize:10,padding:"2px 8px",borderRadius:5,background:"#DC2626",color:"#fff",border:"none",cursor:"pointer",fontWeight:600}}>Sí</button>
                                    <button onClick={()=>setConfirmDelId(null)} style={{fontSize:10,padding:"2px 8px",borderRadius:5,background:"#f1f5f9",color:"#64748b",border:"none",cursor:"pointer"}}>No</button>
                                  </>
                                ) : (
                                  <button onClick={()=>setConfirmDelId(a.id)} style={{fontSize:10,padding:"2px 8px",borderRadius:5,background:"#FEE2E2",color:"#DC2626",border:"none",cursor:"pointer",fontWeight:600}}>×</button>
                                )}
                              </div>
                            </div>
                            <p style={{fontSize:12,color:"#1e293b",margin:"0 0 4px"}}>{a.descripcion}</p>
                            {a.resultado&&<p style={{fontSize:11,color:"#64748b",margin:"0 0 4px",fontStyle:"italic"}}>→ {a.resultado}</p>}
                            {a.proximoContacto&&<span style={{fontSize:10,fontWeight:500,padding:"2px 7px",borderRadius:5,background:"#f1f5f9",color:"#64748b"}}>📅 Acordado: {fmtFecha(a.proximoContacto)}</span>}
                          </>
                        )}
                        {editando && (
                          <div style={{display:"flex",flexDirection:"column",gap:8}}>
                            <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                              {["Llamada","WhatsApp","Email","Reunión","Visita en obra","Otro"].map(t=>(
                                <button key={t} onClick={()=>setEditActForm(f=>({...f,tipo:t}))} style={{
                                  fontSize:11,padding:"3px 10px",borderRadius:6,border:"none",cursor:"pointer",fontWeight:600,
                                  background:editActForm.tipo===t?"#7C3AED":"#f1f5f9",
                                  color:editActForm.tipo===t?"#fff":"#475569",
                                }}>{TIPO_ICON[t]} {t}</button>
                              ))}
                            </div>
                            <div style={{display:"flex",gap:8}}>
                              <div style={{flex:1}}>
                                <div style={{fontSize:10,color:"#94a3b8",marginBottom:2}}>Fecha</div>
                                <input type="date" value={editActForm.fecha} onChange={e=>setEditActForm(f=>({...f,fecha:e.target.value}))}
                                  style={{width:"100%",padding:"6px 8px",border:"1px solid #e2e8f0",borderRadius:6,fontSize:12,fontFamily:"inherit",boxSizing:"border-box"}} />
                              </div>
                              <div style={{flex:1}}>
                                <div style={{fontSize:10,color:"#94a3b8",marginBottom:2}}>Próximo contacto</div>
                                <input type="date" value={editActForm.proximoContacto} onChange={e=>setEditActForm(f=>({...f,proximoContacto:e.target.value}))}
                                  style={{width:"100%",padding:"6px 8px",border:"1px solid #e2e8f0",borderRadius:6,fontSize:12,fontFamily:"inherit",boxSizing:"border-box"}} />
                              </div>
                            </div>
                            <textarea value={editActForm.descripcion} onChange={e=>setEditActForm(f=>({...f,descripcion:e.target.value}))}
                              placeholder="¿Qué pasó?" rows={2}
                              style={{width:"100%",padding:"7px 10px",border:"1px solid #e2e8f0",borderRadius:6,fontSize:12,fontFamily:"inherit",resize:"none",outline:"none",boxSizing:"border-box"}} />
                            <input value={editActForm.resultado} onChange={e=>setEditActForm(f=>({...f,resultado:e.target.value}))}
                              placeholder="Resultado / acuerdo (opcional)"
                              style={{width:"100%",padding:"6px 10px",border:"1px solid #e2e8f0",borderRadius:6,fontSize:12,fontFamily:"inherit",outline:"none",boxSizing:"border-box"}} />
                            <div style={{display:"flex",justifyContent:"flex-end",gap:8}}>
                              <button onClick={()=>setEditActId(null)} style={{fontSize:11,padding:"5px 12px",borderRadius:6,background:"#f1f5f9",color:"#475569",border:"none",cursor:"pointer"}}>Cancelar</button>
                              <button onClick={async()=>{ await actualizarActividad(a.id,editActForm); setEditActId(null); }}
                                style={{fontSize:11,padding:"5px 14px",borderRadius:6,background:"#7C3AED",color:"#fff",border:"none",cursor:"pointer",fontWeight:700}}>
                                Guardar
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
          </div>
        )}

        {/* ── PEDIDOS ── */}
        {tab==="Pedidos" && (
          pedidosCli.length===0 ? (
            <div style={{textAlign:"center",padding:32,color:"#94a3b8"}}>Sin pedidos para este cliente.</div>
          ) : (
            <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
              <thead>
                <tr style={{background:"#f8fafc",borderBottom:"1px solid #e2e8f0"}}>
                  {["Código","Valor","Estado","Pago","Creado"].map(h=>(
                    <th key={h} style={{textAlign:"left",padding:"8px 10px",fontSize:10,fontWeight:600,color:"#94a3b8"}}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {pedidosCli.map(p=>{
                  const ep=ESTADO_PED[p.estado]||{bg:"#f1f5f9",color:"#475569"};
                  return (
                    <tr key={p.id} style={{borderBottom:"0.5px solid #f1f5f9"}}>
                      <td style={{padding:"8px 10px",fontWeight:700,color:"#1E3A5F"}}>{p.codigoSeguimiento||`#${p.id.slice(-4).toUpperCase()}`}</td>
                      <td style={{padding:"8px 10px",fontWeight:700}}>{fmtMoney(p.valor)}</td>
                      <td style={{padding:"8px 10px"}}><span style={{fontSize:10,fontWeight:600,padding:"2px 7px",borderRadius:7,...ep}}>{p.estado}</span></td>
                      <td style={{padding:"8px 10px"}}><span style={{fontSize:10,fontWeight:600,padding:"2px 7px",borderRadius:5,background:p.pagado?"#E1F5EE":"#FEF9C3",color:p.pagado?"#0F6E56":"#854F0B"}}>{p.pagado?"✓ Pagado":"Pendiente"}</span></td>
                      <td style={{padding:"8px 10px",color:"#475569"}}>{fmtTS(p.creadoEn)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )
        )}

        {/* ── HISTORIAL ── */}
        {tab==="Historial" && (
          histCli.length===0 ? (
            <div style={{textAlign:"center",padding:32,color:"#94a3b8"}}>Sin registros de cierres o pérdidas.</div>
          ) : (
            <div style={{display:"flex",flexDirection:"column",gap:8}}>
              {histCli.map(h=>(
                <div key={h.id} style={{display:"flex",gap:12,alignItems:"flex-start",padding:"10px 0",borderBottom:"0.5px solid #f1f5f9"}}>
                  <div style={{width:8,height:8,borderRadius:"50%",background:ESTADOS_GANADA_COT.includes(h.estadoFinal)?"#1D9E75":"#DC2626",flexShrink:0,marginTop:4}}/>
                  <div style={{flex:1}}>
                    <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:2}}>
                      <span style={{fontSize:12,fontWeight:600,color:"#1e293b"}}>{h.proyecto||h.nombre}</span>
                      {h.numeroCot&&<span style={{fontSize:9,fontWeight:700,color:"#3A3C7A",background:"#EEF2FF",padding:"1px 5px",borderRadius:4}}>{h.numeroCot}</span>}
                      <span style={{fontSize:11,fontWeight:700,color:ESTADOS_GANADA_COT.includes(h.estadoFinal)?"#1D9E75":"#DC2626"}}>{h.estadoFinal}</span>
                    </div>
                    <div style={{fontSize:10,color:"#64748b"}}>{fmtTS(h.fecha)} · {h.responsable||"—"}</div>
                    {h.comentario&&<div style={{fontSize:11,color:"#475569",marginTop:3,fontStyle:"italic",background:"#f8fafc",padding:"3px 7px",borderRadius:4}}>"{h.comentario}"</div>}
                  </div>
                  <div style={{fontSize:13,fontWeight:700,color:ESTADOS_GANADA_COT.includes(h.estadoFinal)?"#1D9E75":"#DC2626",flexShrink:0}}>{fmtMoney(h.valor)}</div>
                </div>
              ))}
            </div>
          )
        )}

        {/* ── REUNIONES ── */}
        {tab==="Reuniones" && (
          <div>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
              <div style={{fontSize:12,color:"#64748b"}}>{reuniones.length} reunión{reuniones.length!==1?"es":""}{inteligencia.probProm>0?` · Prob. promedio: ${inteligencia.probProm}%`:""}</div>
              <Link to="/reuniones" style={{fontSize:12,fontWeight:600,padding:"6px 14px",borderRadius:8,background:"#7C3AED",color:"#fff",textDecoration:"none"}}>
                + Nueva reunión
              </Link>
            </div>
            {reuniones.length===0 ? (
              <div style={{textAlign:"center",padding:32,color:"#94a3b8"}}>Sin reuniones con este cliente.</div>
            ) : (
              <div style={{display:"flex",flexDirection:"column",gap:8}}>
                {reuniones.map(r=>{
                  const prob = r.probabilidadCierre||0;
                  const pc   = prob>=70?"#1D9E75":prob>=40?"#D97706":"#DC2626";
                  const pbg  = prob>=70?"#E1F5EE":prob>=40?"#FAEEDA":"#FCEBEB";
                  return (
                    <div key={r.id} style={{background:"#f8fafc",borderRadius:10,padding:"12px 14px",border:"0.5px solid #e2e8f0"}}>
                      <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:6,flexWrap:"wrap"}}>
                        <span style={{fontSize:12,fontWeight:700,color:"#1e293b"}}>{fmtFecha(r.fecha)}</span>
                        <span style={{fontSize:10,fontWeight:600,padding:"2px 7px",borderRadius:8,background:"#EDE9FE",color:"#7C3AED"}}>{r.tipo||"Reunión"}</span>
                        <span style={{fontSize:10,fontWeight:700,padding:"2px 7px",borderRadius:8,background:pbg,color:pc}}>{prob}% cierre</span>
                        {r.responsable&&<span style={{fontSize:10,color:"#94a3b8"}}>· {r.responsable}</span>}
                        {r.proximoContacto&&<span style={{fontSize:10,fontWeight:600,color:"#7C3AED",marginLeft:"auto"}}>→ {fmtFecha(r.proximoContacto)}</span>}
                      </div>
                      <div style={{display:"flex",flexWrap:"wrap",gap:4}}>
                        {r.dolores?.map(d=><span key={d} style={{fontSize:10,fontWeight:600,padding:"2px 7px",borderRadius:8,background:"#FCEBEB",color:"#A32D2D"}}>⚡ {d}</span>)}
                        {r.objeciones?.map(o=><span key={o} style={{fontSize:10,fontWeight:600,padding:"2px 7px",borderRadius:8,background:"#FAEEDA",color:"#854F0B"}}>🛡 {o}</span>)}
                        {r.prioridades?.map(p=><span key={p} style={{fontSize:10,fontWeight:600,padding:"2px 7px",borderRadius:8,background:"#E1F5EE",color:"#0F6E56"}}>★ {p}</span>)}
                      </div>
                      {r.proximasCompras?.length>0 && (
                        <div style={{marginTop:5,display:"flex",flexWrap:"wrap",gap:4}}>
                          {r.proximasCompras.map((c,i)=><span key={i} style={{fontSize:10,fontWeight:600,padding:"2px 7px",borderRadius:8,background:"#EEF2FF",color:"#3A3C7A"}}>📦 {c.material}{c.cantidad?` · ${c.cantidad}`:""}</span>)}
                        </div>
                      )}
                      {r.notas&&<p style={{fontSize:11,color:"#64748b",margin:"6px 0 0",fontStyle:"italic"}}>📝 {r.notas}</p>}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ── PERFIL 360° ── */}
        {tab==="Perfil 360°" && (
          <div style={{display:"flex",flexDirection:"column",gap:20}}>

            {/* BLOQUE A — Identidad */}
            <div>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
                <div style={{fontSize:11,fontWeight:700,color:"#3A3C7A",textTransform:"uppercase",letterSpacing:"0.06em"}}>Identidad de la constructora</div>
                {!editandoPerfil ? (
                  <button onClick={()=>setEditandoPerfil(true)} style={{fontSize:11,padding:"4px 12px",borderRadius:6,background:"#EEF2FF",color:"#3A3C7A",border:"none",cursor:"pointer",fontWeight:600}}>Editar</button>
                ) : (
                  <div style={{display:"flex",gap:6}}>
                    <button onClick={()=>setEditandoPerfil(false)} style={{fontSize:11,padding:"4px 10px",borderRadius:6,background:"#f1f5f9",color:"#475569",border:"none",cursor:"pointer"}}>Cancelar</button>
                    <button onClick={guardarPerfil} disabled={guardandoPerfil} style={{fontSize:11,padding:"4px 12px",borderRadius:6,background:"#3A3C7A",color:"#fff",border:"none",cursor:"pointer",fontWeight:700}}>
                      {guardandoPerfil?"Guardando...":"Guardar"}
                    </button>
                  </div>
                )}
              </div>

              {!editandoPerfil ? (
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
                  {[
                    ["Tipo de obra",     (cliente.perfilConstructora?.tipoObra||[]).join(", ")||"—"],
                    ["Tamaño",           cliente.perfilConstructora?.tamano||"—"],
                    ["Especialidad",     cliente.perfilConstructora?.especialidad||"—"],
                    ["Zonas de operación",cliente.perfilConstructora?.zonasOperacion||"—"],
                    ["Presupuesto prom. obra", cliente.perfilConstructora?.presupuestoPromedioObra ? fmtMoney(cliente.perfilConstructora.presupuestoPromedioObra) : "—"],
                  ].map(([l,v])=>(
                    <div key={l} style={{display:"flex",gap:8}}>
                      <span style={{fontSize:11,color:"#94a3b8",width:140,flexShrink:0}}>{l}</span>
                      <span style={{fontSize:12,color:"#1e293b",fontWeight:500}}>{v}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
                  <div>
                    <label style={lbl}>Tipo de obra (multiselect)</label>
                    <div style={{display:"flex",flexWrap:"wrap",gap:5}}>
                      {TIPOS_OBRA.map(t=>{
                        const on = (perfilForm.tipoObra||[]).includes(t);
                        return (
                          <button key={t} type="button" onClick={()=>toggleTipoObra(t)} style={{
                            fontSize:11,padding:"4px 10px",borderRadius:8,cursor:"pointer",border:"1.5px solid",fontWeight:600,
                            borderColor: on?"#3A3C7A":"#e2e8f0",
                            background:  on?"#EEF2FF":"#fff",
                            color:       on?"#3A3C7A":"#64748b",
                          }}>{t}</button>
                        );
                      })}
                    </div>
                  </div>
                  <div>
                    <label style={lbl}>Tamaño de empresa</label>
                    <select value={perfilForm.tamano||""} onChange={e=>setPerfilForm(f=>({...f,tamano:e.target.value}))} style={inp}>
                      <option value="">Seleccionar...</option>
                      {["Pequeña","Mediana","Grande","Corporativa"].map(t=><option key={t}>{t}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={lbl}>Especialidad</label>
                    <input value={perfilForm.especialidad||""} onChange={e=>setPerfilForm(f=>({...f,especialidad:e.target.value}))}
                      placeholder="Ej: Saneamiento, edificios residenciales..." style={inp} />
                  </div>
                  <div>
                    <label style={lbl}>Zonas de operación</label>
                    <input value={perfilForm.zonasOperacion||""} onChange={e=>setPerfilForm(f=>({...f,zonasOperacion:e.target.value}))}
                      placeholder="Ej: Huánuco, Lima, Pasco" style={inp} />
                  </div>
                  <div>
                    <label style={lbl}>Presupuesto promedio por obra (S/)</label>
                    <input type="number" value={perfilForm.presupuestoPromedioObra||""} onChange={e=>setPerfilForm(f=>({...f,presupuestoPromedioObra:Number(e.target.value)}))}
                      placeholder="500000" style={inp} />
                  </div>
                </div>
              )}
            </div>

            <div style={{height:1,background:"#e2e8f0"}} />

            {/* BLOQUE B — Comportamiento */}
            <div>
              <div style={{fontSize:11,fontWeight:700,color:"#3A3C7A",textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:12}}>Comportamiento de compra</div>
              <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:12,marginBottom:16}}>
                {[
                  { l:"Frecuencia de compra",  v: comportamiento.frecuencia ? `cada ${comportamiento.frecuencia} días` : "Sin datos",     c:"#3A3C7A" },
                  { l:"Ticket promedio",        v: comportamiento.ticketProm>0 ? fmtMoney(comportamiento.ticketProm) : "Sin datos",         c:"#1D9E75" },
                  { l:"Tasa de conversión",     v: `${stats.conversion}%`,                                                                 c:Number(stats.conversion)>=30?"#1D9E75":"#DC2626" },
                ].map(k=>(
                  <div key={k.l} style={{background:"#f8fafc",borderRadius:10,padding:"12px 14px",border:"0.5px solid #e2e8f0"}}>
                    <div style={{fontSize:9,color:"#64748b",marginBottom:4}}>{k.l}</div>
                    <div style={{fontSize:18,fontWeight:700,color:k.c}}>{k.v}</div>
                  </div>
                ))}
              </div>

              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16}}>
                <div>
                  <div style={{fontSize:11,color:"#64748b",marginBottom:6,fontWeight:600}}>Scoring comercial</div>
                  <ScoringBar valor={comportamiento.scoring} />
                  <div style={{fontSize:10,color:"#94a3b8",marginTop:4,display:"flex",flexWrap:"wrap",gap:6}}>
                    {comportamiento.componentes.map(c => (
                      <span key={c.label} title={`${c.label}: ${Math.round(c.valor)}/100`}>
                        {c.label} {Math.round(c.valor)}/100 ({c.pesoEfectivo}%)
                      </span>
                    ))}
                  </div>
                  {comportamiento.huboRedistribucion && (
                    <div style={{fontSize:10,color:"#D97706",marginTop:3,fontWeight:600}}>
                      ⓘ Cliente nuevo — sin historial de frecuencia, peso redistribuido entre los demás factores
                    </div>
                  )}
                </div>
                <div>
                  <div style={{fontSize:11,color:"#64748b",marginBottom:6,fontWeight:600}}>Tendencia (últimos 3 meses)</div>
                  <div style={{fontSize:15,fontWeight:700,color:comportamiento.tendencia==="creciendo"?"#1D9E75":comportamiento.tendencia==="bajando"?"#DC2626":"#64748b"}}>
                    {comportamiento.tendencia==="creciendo"?"↑ Creciendo":comportamiento.tendencia==="bajando"?"↓ Bajando":comportamiento.tendencia==="estable"?"→ Estable":"Sin datos suficientes"}
                    {comportamiento.tendenciaPct!==0&&<span style={{fontSize:12,marginLeft:6}}>({comportamiento.tendenciaPct>0?"+":""}{ comportamiento.tendenciaPct}%)</span>}
                  </div>
                  <div style={{fontSize:10,color:"#94a3b8",marginTop:4}}>Comparado vs trimestre anterior</div>
                </div>
              </div>
            </div>

            <div style={{height:1,background:"#e2e8f0"}} />

            {/* BLOQUE C — Inteligencia cualitativa */}
            <div>
              <div style={{fontSize:11,fontWeight:700,color:"#3A3C7A",textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:12}}>
                Inteligencia cualitativa
                <span style={{fontSize:10,fontWeight:400,color:"#94a3b8",marginLeft:6,textTransform:"none",letterSpacing:0}}>
                  basada en {reuniones.length} reunión{reuniones.length!==1?"es":""}
                </span>
              </div>

              {reuniones.length===0 ? (
                <div style={{textAlign:"center",padding:24,color:"#94a3b8",fontSize:12,background:"#f8fafc",borderRadius:10}}>
                  Registra reuniones con este cliente para generar inteligencia automática.
                  <Link to="/reuniones" style={{display:"block",marginTop:8,fontSize:12,fontWeight:600,color:"#7C3AED"}}>+ Registrar reunión</Link>
                </div>
              ) : (
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:16}}>
                  <div>
                    <div style={{fontSize:11,fontWeight:600,color:"#A32D2D",marginBottom:8}}>⚡ Dolores frecuentes</div>
                    {inteligencia.dolores.length===0
                      ? <div style={{fontSize:11,color:"#94a3b8"}}>Ninguno detectado</div>
                      : <FreqBar items={inteligencia.dolores} colorFn={()=>"#DC2626"} />
                    }
                  </div>
                  <div>
                    <div style={{fontSize:11,fontWeight:600,color:"#854F0B",marginBottom:8}}>🛡 Objeciones comunes</div>
                    {inteligencia.objeciones.length===0
                      ? <div style={{fontSize:11,color:"#94a3b8"}}>Ninguna detectada</div>
                      : <FreqBar items={inteligencia.objeciones} colorFn={()=>"#D97706"} />
                    }
                  </div>
                  <div>
                    <div style={{fontSize:11,fontWeight:600,color:"#0F6E56",marginBottom:8}}>★ Prioridades del cliente</div>
                    {inteligencia.prioridades.length===0
                      ? <div style={{fontSize:11,color:"#94a3b8"}}>Ninguna detectada</div>
                      : <FreqBar items={inteligencia.prioridades} colorFn={()=>"#1D9E75"} />
                    }
                  </div>
                </div>
              )}
            </div>

          </div>
        )}

      </div>
    </div>
  );
}
